// ---------------------------------------------------------------------------
// The company's yearly forecast (the budget workbook built each December) and how the year is doing against it:
// revenue, gross profit, fixed costs and the net result, month by month, and when the fixed costs are covered.
// ---------------------------------------------------------------------------
import { z } from 'zod';

const money = z.number().min(0).max(1e12);
const pct = z.number().min(0).max(100);

export const companyForecastSchema = z.object({
  /** Won order value expected in the year. */
  revenue: money,
  /** How revenue makes profit: the material part and its margin, the installation part and its margin. */
  materialSharePct: pct, materialMarginPct: pct, installSharePct: pct, installMarginPct: pct,
  /** Fixed costs a month, by group (salaries are a group total, never per person). */
  monthly: z.object({ salaries: money, partners: money, running: money }).strict(),
  /** Costs paid once a year, in their month (iqama renewals, medical insurance…). */
  yearly: z.array(z.object({ label: z.string().trim().min(1).max(120), amount: money, month: z.number().int().min(1).max(12) }).strict()).max(40),
  /** How the year's revenue is spread over the months: evenly, like the salespeople's quarterly targets, or "auto"
   * (like the targets only when every quarter has one and none is over twice another; else evenly). */
  spread: z.enum(['auto', 'even', 'targets']).default('auto'),
}).strict();
export type CompanyForecast = z.input<typeof companyForecastSchema>;

/**
 * The quarter shares the plan uses: the quarterly targets' shares when asked (or, on "auto", when they cover every
 * quarter evenly enough), else 25% each. Returns which one was used.
 */
export function forecastQuarterShares(spread: CompanyForecast['spread'], targetTotals: readonly number[]): { shares: number[]; used: 'even' | 'targets' } {
  const total = targetTotals.reduce((a, b) => a + b, 0);
  const even = { shares: [0.25, 0.25, 0.25, 0.25], used: 'even' as const };
  if (spread === 'even' || total <= 0) return even;
  const shares = [0, 1, 2, 3].map((i) => (targetTotals[i] ?? 0) / total);
  if (spread === 'targets') return { shares, used: 'targets' };
  const min = Math.min(...shares); const max = Math.max(...shares);
  return min > 0 && max <= min * 2 ? { shares, used: 'targets' } : even;
}
export const companyForecastSaveSchema = z.object({ year: z.number().int().min(2020).max(2100), data: companyForecastSchema }).strict();

/** The share of revenue that is gross profit (85% material at 10% + 15% installation at 100% = 23.5%). */
export const forecastGpRate = (f: CompanyForecast) => (f.materialSharePct * f.materialMarginPct + f.installSharePct * f.installMarginPct) / 10_000;
/** Fixed costs of one month (1-12): the monthly groups plus that month's yearly items. */
export const forecastFixed = (f: CompanyForecast, month: number) =>
  f.monthly.salaries + f.monthly.partners + f.monthly.running + f.yearly.filter((y) => y.month === month).reduce((a, y) => a + y.amount, 0);

export interface ForecastMonth {
  month: string;
  plan: { revenue: number; gp: number; fixed: number; net: number };
  /** Null for months still to come. */
  actual: { won: number; gp: number; gpEstimated: number; fixed: number; net: number } | null;
  /** The DR-Danat P&L in Odoo (income, expenses, profit), when the nightly copy has the month. */
  odoo: { income: number; expenses: number; profit: number } | null;
}
export interface ForecastActualMonth { month: string; won: number; gp: number; gpEstimated: number }

/**
 * The year month by month: the plan (the yearly revenue spread by `quarterShares`, a third of a quarter a month; its
 * gross profit at the forecast's rate; the fixed costs) against what happened (won, gross profit, the same fixed costs:
 * the CRM doesn't hold actual salaries). `upTo` (YYYY-MM) is the last month with actuals.
 */
export function forecastMonths(year: number, f: CompanyForecast, quarterShares: readonly number[], actuals: ForecastActualMonth[], upTo: string,
  odoo: { month: string; income: number; expenses: number; profit: number }[] = []): ForecastMonth[] {
  const rate = forecastGpRate(f);
  return Array.from({ length: 12 }, (_, i) => {
    const month = `${year}-${String(i + 1).padStart(2, '0')}`;
    const revenue = (f.revenue * (quarterShares[Math.floor(i / 3)] ?? 0.25)) / 3;
    const fixed = forecastFixed(f, i + 1);
    const a = actuals.find((x) => x.month === month);
    const o = odoo.find((x) => x.month === month);
    return {
      month,
      plan: { revenue, gp: revenue * rate, fixed, net: revenue * rate - fixed },
      actual: month <= upTo ? { won: a?.won ?? 0, gp: a?.gp ?? 0, gpEstimated: a?.gpEstimated ?? 0, fixed, net: (a?.gp ?? 0) - fixed } : null,
      odoo: o ? { income: o.income, expenses: o.expenses, profit: o.profit } : null,
    };
  });
}

/**
 * Whether the year's gross profit so far covers the fixed costs so far, and since when for good: the month the running
 * gross profit last climbed back to the running fixed costs (a year that dipped below in June and recovered in October
 * is covered since October). Null when not covered now, with how much more gross profit that needs.
 */
export function forecastBreakEven(months: ForecastMonth[]): { month: string | null; gap: number } {
  let gp = 0; let fixed = 0; let since: string | null = null;
  for (const m of months) {
    if (!m.actual) break;
    gp += m.actual.gp; fixed += m.actual.fixed;
    if (gp >= fixed && fixed > 0) { if (!since) since = m.month; } else since = null;
  }
  return { month: since, gap: Math.max(0, fixed - gp) };
}
